import { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, LifeBuoy, CalendarClock, Sparkles, Terminal, Copy, Check,
  CircleDot, MapPin, Clock, Briefcase, AlertTriangle, FileDown,
} from "lucide-react";
import { downloadInvoicePdf } from "@/lib/push";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const PRIORITY_COLOR = {
  Low: "text-emerald-400 border-emerald-400/40",
  Medium: "text-amber-400 border-amber-400/40",
  High: "text-orange-400 border-orange-400/40",
  Critical: "text-red-400 border-red-400/40",
};

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">{label}</span>
      <span className="text-sm text-[var(--text-primary)]">{value}</span>
    </div>
  );
}

function SaveRef({ refNo, accent }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(refNo);
    setCopied(true);
    toast.success(`${refNo} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div data-testid="save-ref-note" className="flex items-center gap-3 px-4 py-3 border-t border-dashed border-[var(--border)] bg-black/20">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">Save this number</p>
        <p className="text-xs text-[var(--text-secondary)]">Quote <span className="font-mono-tech font-bold" style={{ color: accent }}>{refNo}</span> whenever you contact it IN A bit about this.</p>
      </div>
      <button data-testid="save-ref-copy" onClick={copy} title="Copy reference"
        className="shrink-0 flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors duration-200 hover:border-[var(--primary)] hover:text-white">
        {copied ? <Check size={13} className="text-[var(--tech-accent)]" /> : <Copy size={13} />} Copy
      </button>
    </div>
  );
}

function CardShell({ icon: Icon, accent, title, ref_, children, testid, saveRef }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
      data-testid={testid}
      className="mt-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden shadow-lg shadow-black/30"
    >
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[var(--border)]" style={{ background: `linear-gradient(90deg, ${accent}18, transparent)` }}>
        <div className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: `${accent}22` }}>
          <Icon size={15} style={{ color: accent }} />
        </div>
        <span className="font-display font-semibold text-sm flex-1">{title}</span>
        {ref_ && <span className="font-mono-tech text-xs px-2 py-0.5 rounded-md bg-black/30 border border-[var(--border)]" style={{ color: accent }}>{ref_}</span>}
      </div>
      <div className="p-4">{children}</div>
      {saveRef && ref_ && <SaveRef refNo={ref_} accent={accent} />}
    </motion.div>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid="copy-command-button"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Command copied");
        setTimeout(() => setCopied(false), 1500);
      }}
      className="shrink-0 text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--primary)]"
    >
      {copied ? <Check size={14} className="text-[var(--tech-accent)]" /> : <Copy size={14} />}
    </button>
  );
}

export function InlineCard({ card, onPickSlot }) {
  const { type, data, ref, timekeeper } = card;

  if (type === "warranty") {
    return (
      <CardShell testid="card-warranty" icon={ShieldCheck} accent="#EF4444" title="Warranty Claim" ref_={ref} saveRef>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Device" value={data.device} />
          <Field label="Status" value={data.status} />
          <Field label="Customer" value={data.customer_name} />
          <Field label="Contact" value={data.contact} />
          <div className="col-span-2"><Field label="Issue" value={data.issue} /></div>
        </div>
      </CardShell>
    );
  }

  if (type === "ticket") {
    return (
      <CardShell testid="card-ticket" icon={LifeBuoy} accent="#FF6B00" title="Support Ticket" ref_={ref} saveRef>
        <div className="flex items-center gap-2 mb-3">
          <span className="font-medium text-[var(--text-primary)]">{data.subject}</span>
          <Badge variant="outline" className={`ml-auto font-mono-tech text-[10px] ${PRIORITY_COLOR[data.priority] || ""}`}>{data.priority}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" value={data.category} />
          <div className="col-span-2"><Field label="Description" value={data.description} /></div>
        </div>
      </CardShell>
    );
  }

  if (type === "booking") {
    return (
      <CardShell testid="card-booking" icon={CalendarClock} accent="#FFB000" title="Appointment Booked" ref_={ref} saveRef>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Service" value={data.service} />
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">Location</span>
            <span className="text-sm flex items-center gap-1"><MapPin size={12} className="text-[var(--secondary-accent)]" />{data.location || "—"}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">Date</span>
            <span className="text-sm flex items-center gap-1"><CalendarClock size={12} className="text-[var(--secondary-accent)]" />{data.date}</span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--text-muted)]">Time</span>
            <span className="text-sm flex items-center gap-1"><Clock size={12} className="text-[var(--secondary-accent)]" />{data.time}</span>
          </div>
          <Field label="Customer" value={data.customer_name} />
          <Field label="Job Type" value={data.job_type} />
          <Field label="Email" value={data.email} />
          <Field label="Phone" value={data.phone} />
          {data.address && <div className="col-span-2"><Field label="Address" value={data.address} /></div>}
        </div>
        {timekeeper && (
          <div className="mt-3 pt-3 border-t border-[var(--border)] flex items-center gap-2" data-testid="timekeeper-sync-status">
            {timekeeper.synced ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono-tech text-[var(--tech-accent)]">
                <Check size={13} /> Scheduled in it IN A bit{timekeeper.appointment_id ? ` · ${String(timekeeper.appointment_id).slice(0, 8)}` : ""}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono-tech text-[var(--text-muted)]">
                <MapPin size={13} /> {timekeeper.reason === "not_configured" ? "Not yet linked to it IN A bit" : "Saved locally — it IN A bit sync failed"}
              </span>
            )}
          </div>
        )}
      </CardShell>
    );
  }

  if (type === "lead") {
    return (
      <CardShell testid="card-lead" icon={Sparkles} accent="#FF8A33" title="Sales Lead Captured" ref_={ref}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" value={data.name} />
          <Field label="Company" value={data.company} />
          <Field label="Contact" value={data.contact} />
          <Field label="Interest" value={data.interest} />
          <div className="col-span-2"><Field label="Notes" value={data.notes} /></div>
        </div>
      </CardShell>
    );
  }

  if (type === "diagnostic") {
    return (
      <CardShell testid="card-diagnostic" icon={Terminal} accent="#FF9E1B" title={data.title || "Diagnostic Playbook"} ref_={ref}>
        <div className="space-y-2.5">
          {(data.steps || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-[var(--border)] bg-black/25 p-3">
              <div className="flex items-start gap-2">
                <CircleDot size={14} className="text-[var(--tech-accent)] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)]">{s.step}</p>
                  {s.command && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#0A0E14] border border-[var(--border)] px-3 py-2">
                      <code className="font-mono-tech text-xs text-[var(--tech-accent)] flex-1 overflow-x-auto whitespace-pre">{s.command}</code>
                      <CopyBtn text={s.command} />
                    </div>
                  )}
                  {s.expected && <p className="mt-1.5 text-xs text-[var(--text-muted)]">Expected: {s.expected}</p>}
                </div>
              </div>
            </div>
          ))}
          {data.resolution && (
            <div className="rounded-xl border border-[var(--tech-accent)]/30 bg-[var(--tech-accent)]/10 p-3">
              <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--tech-accent)]">Resolution</span>
              <p className="text-sm text-[var(--text-primary)] mt-1">{data.resolution}</p>
            </div>
          )}
        </div>
      </CardShell>
    );
  }

  if (type === "booking_unavailable") {
    const n = card.next_available || {};
    return (
      <CardShell testid="card-booking-unavailable" icon={AlertTriangle} accent="#FFB000" title="That time is taken">
        <div className="grid grid-cols-2 gap-3">
          <Field label="You asked for" value={`${data.date || ""} ${data.time || ""}`.trim()} />
          <Field label="Next free slot" value={n.time ? (n.label || `${n.date || data.date} ${n.time}`) : "Ask Apex for another time"} />
        </div>
        {(card.options || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-options">
            {card.options.map((o, i) => (
              <button key={`${o.date}-${o.time}`} data-testid={`slot-option-${i}`} disabled={!onPickSlot}
                onClick={() => onPickSlot && onPickSlot(`Yes please, book ${o.label} (${o.date} ${o.time}) instead.`)}
                className="flex items-center gap-1.5 rounded-xl border border-[#FFB000]/60 bg-[#FFB000]/10 px-3 py-2 text-xs font-semibold text-[#FFB000] transition-colors duration-200 hover:bg-[#FFB000]/25 disabled:opacity-50">
                <Clock size={13} /> {o.label}
              </button>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-[var(--text-muted)]">Nothing has been booked yet. Tap a time, or tell Apex another day or time.</p>
      </CardShell>
    );
  }

  if (type === "job") return <JobCard card={card} />;
  if (type === "invoice_pdf") return <InvoicePdfButton card={card} />;

  return null;
}

function InvoicePdfButton({ card }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await downloadInvoicePdf(card.start, card.end); }
    catch (e) { toast.error("Couldn't create the PDF", { description: e?.response?.status === 403 ? "Accounts or technician login needed" : e.message }); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="invoice-pdf-download" onClick={go} disabled={busy}
      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[var(--primary)] bg-[var(--primary)]/10 px-4 py-2 text-sm font-semibold text-[var(--primary)] transition-colors duration-200 hover:bg-[var(--primary)]/20 disabled:opacity-60">
      <FileDown size={15} /> {busy ? "Creating PDF…" : `Download PDF (${card.label})`}
    </button>
  );
}

function JobCard({ card }) {
  const { ok, action, data = {}, time_logged: t, reason } = card;
  const accent = ok ? "#FF6B00" : "#EF4444";
  const title = ok ? `Job ${action}` : "Timekeeper update failed";
  return (
    <CardShell testid="card-job" icon={ok ? Briefcase : AlertTriangle} accent={accent} title={title} ref_={card.ref}>
      {ok ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer" value={data.customer_name} />
          <Field label="Status" value={data.status} />
          <div className="col-span-2"><Field label="Job" value={data.description} /></div>
          <Field label="Date" value={data.appointment_date} />
          <Field label="Times" value={data.start_time && `${data.start_time}–${data.end_time}`} />
          {t && (
            <div className="col-span-2 rounded-xl border border-[var(--tech-accent)]/30 bg-[var(--tech-accent)]/10 p-3" data-testid="job-card-time-logged">
              <span className="text-[10px] font-mono-tech uppercase tracking-widest text-[var(--tech-accent)]">Time logged</span>
              <p className="text-sm text-[var(--text-primary)] mt-1 flex items-center gap-1.5">
                <Clock size={13} /> {t.date} · {t.start_time}–{t.stop_time} · {t.hours}h
              </p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-[var(--text-muted)]" data-testid="job-card-error">{reason || "Something went wrong."}</p>
      )}
    </CardShell>
  );
}
